import { useForm } from 'react-hook-form'
import { useEffect, useState, useRef } from 'react'
import { ExaminationRoom } from '@/pages/clinic/examinationRooms/columns'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { logger } from '@magnetar/utils'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/hooks/useAuth'
import { appointmentService } from '@/services/appointment_service'
import { toast } from 'sonner'
import { useExaminationRoomAppointmentSetting } from '@/hooks/useAppointmentSetting'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
// import { AppointmentType } from '@magnetar/admin-api-interfaces'
import { Checkbox } from '@/components/ui/checkbox'

enum AppointmentType {
  SEQUENTIAL = 'SEQUENTIAL', // 流水號類型
  TIME_BASED = 'TIME_BASED', // 時間類型
}

// 流水號預約掛號設定表單欄位型別
type SequentialFormValues = {
  appointmentType: AppointmentType.SEQUENTIAL
  maxAppointments: number
  sequentialType: 'REGULAR' | 'ODD_EVEN'
  startNumber: number
  incrementBy: number
  onlineEven?: boolean
  walkInOdd?: boolean
  onlineNumberType?: 'EVEN' | 'ODD'
  walkInNumberType?: 'EVEN' | 'ODD'
}

// 時間型預約掛號設定表單欄位型別
type TimeBasedFormValues = {
  appointmentType: AppointmentType.TIME_BASED
  dayStartTime: string
  dayEndTime: string
  isActive: boolean
  timeSlotType: 'FREE' | 'FIXED'
  slotDurationMinutes: number
  bufferMinutes: number
  allowOverlap?: boolean
}

type AppointmentSettingProps = {
  examinationRoom?: ExaminationRoom
  isLoading?: boolean
  onSuccess?: () => void
}

export function AppointmentSetting({
  examinationRoom,
  isLoading,
  onSuccess,
}: AppointmentSettingProps) {
  const { token } = useAuth()
  const [activeTab, setActiveTab] = useState<'sequential' | 'time-based'>('sequential')

  // 模式切換相關狀態
  const [pendingModeChange, setPendingModeChange] = useState<'DEFAULT' | 'SHARED_ROOMS' | null>(
    null,
  )
  const [isSwitchingMode, setIsSwitchingMode] = useState(false)

  // 使用 ref 來追踪表單是否已經設置過值
  const hasSetFormValues = useRef(false)

  // 獲取診間的預約掛號設定
  const {
    appointmentSettings,
    isLoading: isLoadingSettings,
    mutate: mutateSettings,
  } = useExaminationRoomAppointmentSetting(examinationRoom?.examination_room_id)

  // 當前 setting 與模式
  const currentSetting = appointmentSettings?.[0]
  const currentMode = currentSetting?.appointment_mode ?? 'DEFAULT'
  const sharedRoomCount = currentSetting?.examination_rooms?.length ?? 0

  // 觸發模式切換確認對話框
  const handleModeChange = (newMode: 'DEFAULT' | 'SHARED_ROOMS') => {
    if (newMode === currentMode) return
    if (!currentSetting) {
      toast.error('請先儲存任一份預約掛號設定，再切換模式')
      return
    }
    setPendingModeChange(newMode)
  }

  // 確認執行模式切換
  const confirmModeChange = async () => {
    if (!pendingModeChange || !currentSetting || !token) return
    setIsSwitchingMode(true)
    try {
      const response = await appointmentService.switchMode(
        currentSetting.appointment_setting_id,
        pendingModeChange,
        token,
      )
      if (response.success) {
        toast.success(
          pendingModeChange === 'SHARED_ROOMS' ? '已切換為診間共用模式' : '已切換為各診間獨立模式',
        )
        mutateSettings()
        onSuccess?.()
      } else {
        toast.error('模式切換失敗', { description: response.error?.message })
      }
    } catch (error) {
      toast.error('模式切換失敗', {
        description: error instanceof Error ? error.message : undefined,
      })
    } finally {
      setIsSwitchingMode(false)
      setPendingModeChange(null)
    }
  }

  // 流水號預約掛號設定表單
  const sequentialForm = useForm<SequentialFormValues>({
    // resolver: zodResolver(sequentialFormSchema),
    defaultValues: {
      appointmentType: AppointmentType.SEQUENTIAL,
      maxAppointments: 20,
      sequentialType: 'REGULAR',
      startNumber: 1,
      incrementBy: 1,
      onlineEven: false,
      walkInOdd: false,
      onlineNumberType: 'EVEN', // 新增：線上預約使用偶數或奇數
      walkInNumberType: 'ODD', // 新增：現場掛號使用奇數或偶數
    },
  })

  // 時間型預約掛號設定表單
  const timeBasedForm = useForm<TimeBasedFormValues>({
    // resolver: zodResolver(timeBasedFormSchema),
    defaultValues: {
      appointmentType: AppointmentType.TIME_BASED,
      dayEndTime: '18:00',
      isActive: true,
      timeSlotType: 'FIXED',
      slotDurationMinutes: 30,
      bufferMinutes: 0, // 預設為 0，啟用固定間隔模式
      allowOverlap: false,
    },
  })

  // 當獲取到預約掛號設定時，更新表單值
  useEffect(() => {
    // 如果沒有設定數據，則不需要做任何事情
    if (!appointmentSettings || appointmentSettings.length === 0) {
      return
    }

    // 如果已經設置過表單值，則不再重複設置
    // 這會避免無限循環更新
    if (hasSetFormValues.current) {
      return
    }

    const setting = appointmentSettings[0]
    logger.log('API 返回的設定數據:', setting)

    if (setting.appointment_type === AppointmentType.SEQUENTIAL) {
      const metadata = setting.metadata || {}

      // 確定奇偶數類型的選擇
      let onlineNumberType: 'EVEN' | 'ODD' = 'EVEN'
      let walkInNumberType: 'EVEN' | 'ODD' = 'ODD'

      // 如果已經有存儲的選擇類型，則使用它
      if (metadata.online_number_type) {
        onlineNumberType = metadata.online_number_type === 'EVEN' ? 'EVEN' : 'ODD'
        walkInNumberType = onlineNumberType === 'EVEN' ? 'ODD' : 'EVEN'
      } else {
        // 否則根據 online_even 和 walk_in_odd 推斷
        if (metadata.sequential_type === 'ODD_EVEN') {
          if (metadata.online_even) {
            onlineNumberType = 'EVEN'
            walkInNumberType = 'ODD'
          } else {
            onlineNumberType = 'ODD'
            walkInNumberType = 'EVEN'
          }
        }
      }

      // 在設置表單值前先打印出所有相關的值
      logger.log('metadata.increment_by:', metadata.increment_by, typeof metadata.increment_by)

      // 設置表單值
      sequentialForm.reset(
        {
          appointmentType: AppointmentType.SEQUENTIAL,
          maxAppointments: setting.max_appointments || 20,
          sequentialType: metadata.sequential_type || 'REGULAR',
          startNumber: metadata.start_number || 1,
          // 確保使用 API 返回的 increment_by 值，如果存在的話
          incrementBy: metadata.increment_by !== undefined ? Number(metadata.increment_by) : 1,
          onlineEven: metadata.online_even || false,
          walkInOdd: metadata.walk_in_odd || false,
          onlineNumberType: onlineNumberType,
          walkInNumberType: walkInNumberType,
        },
        { keepDefaultValues: true },
      )

      // 設置標籤頁
      setActiveTab('sequential')
    } else if (setting.appointment_type === AppointmentType.TIME_BASED) {
      const metadata = setting.metadata || {}

      // 設置表單值
      timeBasedForm.reset(
        {
          appointmentType: AppointmentType.TIME_BASED,
          dayStartTime: setting.day_start_time || '09:00',
          dayEndTime: setting.day_end_time || '17:00',
          timeSlotType: metadata.time_slot_type || 'FIXED',
          slotDurationMinutes: metadata.slot_duration_minutes || 30,
          bufferMinutes: metadata.buffer_minutes || 0,
          allowOverlap: metadata.allow_overlap || false,
        },
        { keepDefaultValues: true },
      )

      // 設置標籤頁
      setActiveTab('time-based')
    }

    // 標記已經設置過表單值
    hasSetFormValues.current = true
    // sequentialForm / timeBasedForm 為 react-hook-form 的穩定參考，
    // 加入後仍由 hasSetFormValues 與 appointmentSettings 控制只執行一次
  }, [appointmentSettings, sequentialForm, timeBasedForm])

  // 提交流水號預約掛號設定
  const onSubmitSequential = async (values: SequentialFormValues) => {
    if (!token || !examinationRoom) return

    try {
      // 根據新的選擇類型設定 online_even 和 walk_in_odd 的值
      const onlineEven =
        values.sequentialType === 'ODD_EVEN' ? values.onlineNumberType === 'EVEN' : undefined
      const walkInOdd =
        values.sequentialType === 'ODD_EVEN' ? values.walkInNumberType === 'ODD' : undefined

      const data = {
        examinationRoomId: examinationRoom.examination_room_id,
        appointmentType: AppointmentType.SEQUENTIAL,
        maxAppointments: values.maxAppointments,
        metadata: {
          sequential_type: values.sequentialType,
          start_number: values.startNumber,
          increment_by: values.incrementBy,
          online_even: onlineEven,
          walk_in_odd: walkInOdd,
          // 保存選擇的類型，以便後續顯示
          online_number_type:
            values.sequentialType === 'ODD_EVEN' ? values.onlineNumberType : undefined,
          walk_in_number_type:
            values.sequentialType === 'ODD_EVEN' ? values.walkInNumberType : undefined,
        },
      }

      // 如果已有設定，則更新
      if (appointmentSettings && appointmentSettings.length > 0) {
        const setting = appointmentSettings[0]
        const response = await appointmentService.updateAppointmentSetting(
          setting.appointment_setting_id,
          data,
          token ?? '',
        )

        if (response.success) {
          toast.success('更新預約掛號設定成功')
          mutateSettings()
          onSuccess?.()
        } else {
          toast.error('更新預約掛號設定失敗', {
            description: response.error?.message,
          })
        }
      } else {
        // 否則建立新設定
        const response = await appointmentService.createAppointmentSetting(data, token ?? '')

        if (response.success) {
          toast.success('建立預約掛號設定成功')
          mutateSettings()
          onSuccess?.()
        } else {
          toast.error('建立預約掛號設定失敗')
        }
      }
    } catch {
      toast.error('操作失敗')
    }
  }

  // 提交時間型預約掛號設定
  const onSubmitTimeBased = async (values: TimeBasedFormValues) => {
    if (!token || !examinationRoom) return

    // 驗證固定時段模式的設定
    if (values.timeSlotType === 'FIXED') {
      if (!values.slotDurationMinutes) {
        toast.error('請設定時段長度')
        return
      }
    }

    try {
      const data = {
        examinationRoomId: examinationRoom.examination_room_id,
        appointmentType: AppointmentType.TIME_BASED,
        dayStartTime: values.dayStartTime,
        dayEndTime: values.dayEndTime,
        metadata: {
          time_slot_type: values.timeSlotType,
          slot_duration_minutes: values.slotDurationMinutes,
          buffer_minutes: values.bufferMinutes,
          allow_overlap: values.allowOverlap,
        },
      }

      // 如果已有設定，則更新
      if (appointmentSettings && appointmentSettings.length > 0) {
        const setting = appointmentSettings[0]
        const response = await appointmentService.updateAppointmentSetting(
          setting.appointment_setting_id,
          data,
          token ?? '',
        )

        if (response.success) {
          toast.success('更新預約掛號設定成功')
          mutateSettings()
          onSuccess?.()
        } else {
          toast.error('更新預約掛號設定失敗')
        }
      } else {
        // 否則建立新設定
        const response = await appointmentService.createAppointmentSetting(data, token ?? '')

        if (response.success) {
          toast.success('建立預約掛號設定成功')
          mutateSettings()
          onSuccess?.()
        } else {
          toast.error('建立預約掛號設定失敗')
        }
      }
    } catch {
      toast.error('操作失敗')
    }
  }

  if (isLoading || isLoadingSettings) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-1/3" />
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">預約掛號設定</h2>
        <p className="text-sm text-muted-foreground mt-1">
          設定診間的預約方式，包括流水號預約或時間型預約
        </p>
      </div>

      {/* 預約模式切換 — 影響整個診所 */}
      <Card>
        <CardHeader>
          <CardTitle>預約模式</CardTitle>
          <CardDescription>
            ⚠️ 此設定影響整個診所，切換時會重新分配診所內所有診間的預約資料
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={currentMode}
            onValueChange={(val) => handleModeChange(val as 'DEFAULT' | 'SHARED_ROOMS')}
            className="flex flex-col space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="DEFAULT" id="mode-default" />
              <Label htmlFor="mode-default" className="font-normal cursor-pointer">
                各診間獨立預約清單（預設）
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="SHARED_ROOMS" id="mode-shared" />
              <Label htmlFor="mode-shared" className="font-normal cursor-pointer">
                診間共用預約清單
              </Label>
            </div>
          </RadioGroup>
          {currentMode === 'SHARED_ROOMS' && (
            <p className="mt-3 text-sm text-muted-foreground">
              目前診所內 {sharedRoomCount} 間診間共用此預約清單
            </p>
          )}
        </CardContent>
      </Card>

      {/* 模式切換確認對話框 */}
      <AlertDialog
        open={pendingModeChange !== null}
        onOpenChange={(open) => !open && setPendingModeChange(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingModeChange === 'SHARED_ROOMS'
                ? '切換為「診間共用預約清單」？'
                : '切換為「各診間獨立預約清單」？'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingModeChange === 'SHARED_ROOMS' ? (
                <>
                  將以本診間目前的設定作為共用範本：
                  <br />• 診所內所有診間都會共用這份預約清單
                  <br />• 診所內其他診間既有的獨立設定會被停用
                  <br />• 既有預約會自動併入共用清單，原本派發的診間會保留
                </>
              ) : (
                <>
                  將把共用設定複製到每個診間成為獨立版本：
                  <br />• 已派發的預約會跟著各自的診間
                  <br />• 若有「待派發」的預約必須先處理完畢，否則切換會失敗
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSwitchingMode}>取消</AlertDialogCancel>
            <AlertDialogAction onClick={confirmModeChange} disabled={isSwitchingMode}>
              {isSwitchingMode ? '切換中…' : '確認切換'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as 'sequential' | 'time-based')}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="sequential">流水號預約</TabsTrigger>
          <TabsTrigger value="time-based">時間型預約</TabsTrigger>
        </TabsList>

        <TabsContent value="sequential" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>流水號預約掛號設定</CardTitle>
              <CardDescription>
                設定診間使用流水號方式進行預約，可以選擇一般流水號或奇偶數流水號
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...sequentialForm}>
                <form
                  onSubmit={sequentialForm.handleSubmit(onSubmitSequential)}
                  className="space-y-6"
                >
                  <FormField
                    control={sequentialForm.control}
                    name="maxAppointments"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>最大預約數量</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormDescription>每日最多可預約的人數</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <FormField
                    control={sequentialForm.control}
                    name="sequentialType"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel>流水號類型</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex flex-col space-y-1"
                          >
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="REGULAR" />
                              </FormControl>
                              <FormLabel className="font-normal">一般</FormLabel>
                            </FormItem>
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl>
                                <RadioGroupItem value="ODD_EVEN" />
                              </FormControl>
                              <FormLabel className="font-normal">奇數與偶數</FormLabel>
                            </FormItem>
                          </RadioGroup>
                        </FormControl>
                        <FormDescription>
                          選擇一般流水號或奇偶數流水號（奇數和偶數可分別用於不同來源的預約）
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={sequentialForm.control}
                      name="startNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>起始號碼</FormLabel>
                          <FormControl>
                            <Input type="number" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={sequentialForm.control}
                      name="incrementBy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>增量</FormLabel>
                          <FormControl>
                            <Input type="number" {...field} />
                          </FormControl>
                          <FormDescription>每次增加的數量</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {sequentialForm.watch('sequentialType') === 'ODD_EVEN' && (
                    <div className="space-y-4">
                      <FormField
                        control={sequentialForm.control}
                        name="onlineNumberType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>線上預約使用號碼類型</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(value)
                                // 如果選擇了偶數，則現場掛號自動設為奇數
                                if (value === 'EVEN') {
                                  sequentialForm.setValue('walkInNumberType', 'ODD')
                                } else {
                                  // 如果選擇了奇數，則現場掛號自動設為偶數
                                  sequentialForm.setValue('walkInNumberType', 'EVEN')
                                }
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="請選擇號碼類型" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="EVEN">偶數</SelectItem>
                                <SelectItem value="ODD">奇數</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>選擇線上預約使用奇數或偶數流水號</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={sequentialForm.control}
                        name="walkInNumberType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>現場掛號使用號碼類型</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(value)
                                // 如果選擇了偶數，則線上預約自動設為奇數
                                if (value === 'EVEN') {
                                  sequentialForm.setValue('onlineNumberType', 'ODD')
                                } else {
                                  // 如果選擇了奇數，則線上預約自動設為偶數
                                  sequentialForm.setValue('onlineNumberType', 'EVEN')
                                }
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="請選擇號碼類型" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="EVEN">偶數</SelectItem>
                                <SelectItem value="ODD">奇數</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>選擇現場掛號使用奇數或偶數流水號</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  <Button type="submit">儲存</Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="time-based" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>時間型預約掛號設定</CardTitle>
              <CardDescription>
                設定診間使用時間段方式進行預約，可以選擇固定時段或自由時間
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...timeBasedForm}>
                <form
                  onSubmit={timeBasedForm.handleSubmit(onSubmitTimeBased)}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={timeBasedForm.control}
                      name="dayStartTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>開始時間</FormLabel>
                          <FormControl>
                            <Input type="time" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={timeBasedForm.control}
                      name="dayEndTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>結束時間</FormLabel>
                          <FormControl>
                            <Input type="time" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <FormField
                    control={timeBasedForm.control}
                    name="timeSlotType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>時段類型</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="請選擇時段類型" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="FIXED">固定時段</SelectItem>
                            <SelectItem value="FREE">自由時間</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          固定時段：按照固定的時間間隔分配時段；自由時間：可以選擇任意時間
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {timeBasedForm.watch('timeSlotType') === 'FIXED' && (
                    <FormField
                      control={timeBasedForm.control}
                      name="slotDurationMinutes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>時段長度（分鐘）</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={(value) => field.onChange(Number(value))}
                              value={field.value?.toString()}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="選擇時段長度" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="15">15 分鐘</SelectItem>
                                <SelectItem value="30">30 分鐘</SelectItem>
                                <SelectItem value="45">45 分鐘</SelectItem>
                                <SelectItem value="60">60 分鐘</SelectItem>
                                <SelectItem value="90">90 分鐘</SelectItem>
                                <SelectItem value="120">120 分鐘</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormDescription>固定時段模式下的每個時段長度</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {timeBasedForm.watch('timeSlotType') === 'FIXED' && (
                    <FormField
                      control={timeBasedForm.control}
                      name="bufferMinutes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>緩衝時間（分鐘）</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={(value) => field.onChange(Number(value))}
                              value={field.value?.toString()}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="選擇緩衝時間" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="0">無緩衝（固定間隔）</SelectItem>
                                <SelectItem value="5">5 分鐘</SelectItem>
                                <SelectItem value="10">10 分鐘</SelectItem>
                                <SelectItem value="15">15 分鐘</SelectItem>
                                <SelectItem value="30">30 分鐘</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormDescription>
                            {field.value === 0
                              ? '無緩衝時間，系統將生成固定間隔的時間格'
                              : '預約時段之間的緩衝時間'}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {timeBasedForm.watch('timeSlotType') === 'FIXED' &&
                    timeBasedForm.watch('bufferMinutes') === 0 && (
                      <div className="p-4 bg-muted/50 rounded-md">
                        <div className="flex items-center space-x-2 mb-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-4 w-4"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                          </svg>
                          <p className="text-sm font-medium">固定間隔模式已啟用</p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          系統將根據設定的時段長度自動生成固定間隔的時間格。 例如：
                          {timeBasedForm.watch('dayStartTime')} 開始，每{' '}
                          {timeBasedForm.watch('slotDurationMinutes')} 分鐘一個時段，直到{' '}
                          {timeBasedForm.watch('dayEndTime')}。
                        </p>
                      </div>
                    )}

                  <FormField
                    control={timeBasedForm.control}
                    name="allowOverlap"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>允許時段重疊</FormLabel>
                          <FormDescription>啟用後，同一時段可以有多個預約</FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <Button type="submit">儲存</Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
